import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Clock,
  ChevronDown,
  Headphones,
  Shield,
  Zap,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { contactApi } from "@/lib/api";

export const Route = createFileRoute("/_info/contact")({
  head: () => ({
    meta: [
      { title: "Contact Us — OyoBooking" },
      { name: "description", content: "Get in touch with the OyoBooking support and concierge team." },
    ],
    links: [{ rel: "canonical", href: "https://oyobooking.ng/contact" }],
  }),
  component: ContactPage,
});

const CONTACT_CHANNELS = [
  {
    icon: Mail,
    title: "Email Us",
    body: "oyobooking@gmail.com",
    sub: "We'll respond within 24 hours",
    color: "from-rose-500/20 to-orange-500/20",
    iconBg: "bg-gradient-to-br from-rose-500 to-orange-500",
    href: "mailto:oyobooking@gmail.com",
  },
  {
    icon: Phone,
    title: "WhatsApp Us",
    body: "+234 902 299 0000",
    sub: "Mon – Sat, 8am – 8pm WAT",
    color: "from-blue-500/20 to-cyan-500/20",
    iconBg: "bg-gradient-to-br from-blue-500 to-cyan-500",
    href: "https://wa.me/2349022990000",
  },
  {
    icon: MapPin,
    title: "Visit Us",
    body: "Ministry of Culture & Tourism, Oyo State Secretariat",
    sub: "Oyo State, Nigeria",
    color: "from-emerald-500/20 to-teal-500/20",
    iconBg: "bg-gradient-to-br from-emerald-500 to-teal-500",
  },
];

const FAQS = [
  {
    q: "How quickly do you respond to messages?",
    a: "We aim to respond to all enquiries within 24 hours during business days. Phone responses are typically within minutes during operating hours.",
  },
  {
    q: "Can I book a venue over the phone?",
    a: "Absolutely! Our concierge team can walk you through the booking process, help you find the perfect venue, and complete your reservation over the phone.",
  },
  {
    q: "What if I need to modify or cancel a booking?",
    a: "You can manage your bookings through your OyoBooking dashboard. For urgent changes, contact us directly via phone or email and we'll sort it out immediately.",
  },
  {
    q: "Do you offer venue tours before booking?",
    a: "Yes! For event centres and premium hotels, we can arrange guided tours. Simply mention it in your message and we'll coordinate with the venue host.",
  },
];

function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await contactApi.send({
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
      });
      setSent(true);
      toast.success("Message sent — we'll respond within 24 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to send message. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* ── Hero Section ──────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-gold/5 pb-8 pt-16 sm:pb-12 sm:pt-20">
        {/* Decorative floating orbs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <div className="absolute -left-20 top-10 size-72 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -right-20 bottom-10 size-80 rounded-full bg-gold/8 blur-3xl" />
          <div className="absolute left-1/2 top-1/3 size-48 -translate-x-1/2 rounded-full bg-primary/3 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              <Headphones className="size-3.5" />
              Get in touch
            </span>
          </div>

          <h1 className="animate-fade-up delay-75 mt-6 font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
            We're here to{" "}
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                help
              </span>
              <span className="absolute -bottom-1 left-0 h-3 w-full rounded bg-gold/20" aria-hidden="true" />
            </span>{" "}
            you.
          </h1>

          <p className="animate-fade-up delay-150 mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
            Whether you need assistance with a booking, want to list your property, or simply have a question — our team is ready and eager to assist.
          </p>
        </div>
      </section>

      {/* ── Contact Channels ──────────────────────────────────── */}
      <section className="relative -mt-1 bg-gradient-warm">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {CONTACT_CHANNELS.map((c, i) => {
              const delayClass = ["delay-225", "delay-300", "delay-375"][i] || "";
              const href = "href" in c ? c.href : undefined;
              const cardContent = (
                <>
                  {/* Gradient hover glow */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${c.color} opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
                  />
                  <div className="relative">
                    <div
                      className={`${c.iconBg} grid size-12 place-items-center rounded-xl text-white shadow-lg transition-transform duration-300 group-hover:scale-110`}
                    >
                      <c.icon className="size-5" />
                    </div>
                    <h3 className="mt-4 font-display text-lg font-semibold">{c.title}</h3>
                    <p className="mt-1 font-medium text-foreground">{c.body}</p>
                  </div>
                </>
              );

              return href ? (
                <a
                  key={c.title}
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel={href.startsWith("http") ? "noreferrer" : undefined}
                  className={`animate-fade-up ${delayClass} group relative overflow-hidden rounded-2xl border border-border/60 bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-elegant`}
                >
                  {cardContent}
                </a>
              ) : (
                <div
                  key={c.title}
                  className={`animate-fade-up ${delayClass} group relative overflow-hidden rounded-2xl border border-border/60 bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-elegant`}
                >
                  {cardContent}
                </div>
              );
          })}
          </div>
        </div>
      </section>

      {/* ── Main: Form + Sidebar ──────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1fr_420px]">
          {/* Form */}
          <div className="animate-slide-l">
            <div className="mb-8">
              <h2 className="font-display text-3xl font-bold">Send us a message</h2>
              <p className="mt-2 text-muted-foreground">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
            </div>

            <form
              onSubmit={submit}
              className="relative overflow-hidden rounded-2xl border border-border/60 bg-card p-8 shadow-soft"
            >
              {/* Success overlay */}
              {sent && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-card/95 backdrop-blur-sm">
                  <div className="animate-pop grid size-16 place-items-center rounded-full bg-emerald-500/10 text-emerald-600">
                    <Send className="size-7" />
                  </div>
                  <p className="mt-4 font-display text-xl font-semibold">Message sent!</p>
                  <p className="mt-1 text-sm text-muted-foreground">We'll be in touch within 24 hours.</p>
                </div>
              )}

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Full Name</Label>
                  <Input
                    id="contact-name"
                    placeholder="John Doe"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email">Email Address</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    placeholder="john@example.com"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="h-11"
                  />
                </div>
              </div>

              <div className="mt-5 space-y-2">
                <Label htmlFor="contact-subject">Subject</Label>
                <Input
                  id="contact-subject"
                  placeholder="How can we help?"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="h-11"
                />
              </div>

              <div className="mt-5 space-y-2">
                <Label htmlFor="contact-message">Message</Label>
                <Textarea
                  id="contact-message"
                  rows={6}
                  placeholder="Tell us more about your enquiry..."
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                size="lg"
                className="mt-6 gap-2 px-8"
              >
                {loading ? (
                  <>
                    <span className="size-4 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send message
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Sidebar */}
          <div className="animate-slide-r space-y-6">
            {/* Response time card */}
            <div className="rounded-2xl border border-border/60 bg-gradient-to-br from-primary/5 to-gold/5 p-6">
              <div className="flex items-center gap-3">
                <div className="grid size-10 place-items-center rounded-lg bg-primary text-primary-foreground">
                  <Clock className="size-5" />
                </div>
                <div>
                  <h3 className="font-display font-semibold">Average response time</h3>
                  <p className="text-2xl font-bold text-primary">Under 2 hours</p>
                </div>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                During business hours (Mon–Sat, 8am–8pm WAT), we typically respond much faster.
              </p>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: Shield, label: "Secure & Private", sub: "Your data is safe" },
                { icon: Zap, label: "Quick Support", sub: "Fast resolutions" },
              ].map((badge) => (
                <div
                  key={badge.label}
                  className="flex flex-col items-center rounded-2xl border border-border/60 bg-card p-5 text-center transition-all duration-300 hover:shadow-soft"
                >
                  <div className="grid size-10 place-items-center rounded-lg bg-accent text-primary">
                    <badge.icon className="size-5" />
                  </div>
                  <p className="mt-2.5 text-sm font-semibold">{badge.label}</p>
                  <p className="text-xs text-muted-foreground">{badge.sub}</p>
                </div>
              ))}
            </div>

            {/* FAQ Section */}
            <div>
              <h3 className="mb-4 font-display text-xl font-semibold">Frequently asked</h3>
              <div className="space-y-2">
                {FAQS.map((faq, i) => (
                  <div
                    key={i}
                    className="overflow-hidden rounded-xl border border-border/60 bg-card transition-all duration-200"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left text-sm font-medium transition-colors hover:bg-accent/50"
                      {...{ "aria-expanded": openFaq === i }}
                      aria-controls={`faq-answer-${i}`}
                    >
                      <span>{faq.q}</span>
                      <ChevronDown
                        className={`size-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                          openFaq === i ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <div
                      id={`faq-answer-${i}`}
                      className={`grid transition-all duration-200 ${
                        openFaq === i ? "grid-rows-expand" : "grid-rows-collapse"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="px-5 pb-4 text-sm text-muted-foreground">
                          {faq.a}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ────────────────────────────────────────── */}
      <section className="border-t border-border/60 bg-gradient-warm">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold sm:text-3xl">
            Prefer to talk to a real person?
          </h2>
          <p className="mt-3 text-muted-foreground">
            Our concierge team is available six days a week to help you find the perfect venue.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a href="https://wa.me/2349022990000" target="_blank" rel="noreferrer">
              <Button size="lg" className="gap-2">
                <Phone className="size-4" />
                Chat on WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
